import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { getData } from '../../../webService/webservice';
import { toast } from 'react-toastify';

const MessageLogsPage = () => {
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Filters
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [channelFilter, setChannelFilter] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('Token') || localStorage.getItem('Token');
      const params = {
        page: currentPage,
        limit: 20,
        status: statusFilter,
        channel: channelFilter,
        search: searchQuery
      };
      const res = await getData('campaigns/message-logs', params, token);

      if ((res?.status === 200 || res?.success) && res?.data) {
        setLogs(res.data.logs || []);
        setTotalCount(res.data.totalLogsCount || 0);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (err) {
      console.error('Error fetching message logs:', err);
      toast.error('Failed to load message logs');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter, channelFilter, searchQuery]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <div className="product-layout">
      <div style={{ padding: '24px', maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#0f172a', margin: 0 }}>
              WhatsApp & Campaign Message Logs
            </h1>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
              Inspect Meta Graph API request payloads, responses, HTTP status codes, and delivery statuses.
            </p>
          </div>
          <button
            onClick={fetchLogs}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              background: '#ffffff',
              color: '#334155',
              fontWeight: 700,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Icon icon="mdi:refresh" /> Refresh Logs
          </button>
        </div>

        {/* Filter Controls Bar */}
        <div
          style={{
            background: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            alignItems: 'center'
          }}
        >
          {/* Search Input */}
          <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by customer name, phone, message ID..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '13px'
              }}
            />
            <Icon
              icon="mdi:magnify"
              style={{ position: 'absolute', left: '10px', top: '10px', color: '#94a3b8', fontSize: '18px' }}
            />
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
          >
            <option value="ALL">All Delivery Statuses</option>
            <option value="Sent">Sent</option>
            <option value="Delivered">Delivered</option>
            <option value="Read">Read</option>
            <option value="Failed">Failed</option>
          </select>

          {/* Channel Filter */}
          <select
            value={channelFilter}
            onChange={(e) => {
              setChannelFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
          >
            <option value="ALL">All Channels</option>
            <option value="WhatsApp">WhatsApp</option>
            <option value="SMS">SMS</option>
            <option value="Email">Email</option>
          </select>
        </div>

        {/* Logs Table */}
        <div style={{ background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>Loading message logs...</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>No message logs match your filters.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569', fontWeight: 700 }}>
                  <th style={{ padding: '12px 16px' }}>Customer</th>
                  <th style={{ padding: '12px 16px' }}>Contact</th>
                  <th style={{ padding: '12px 16px' }}>Channel</th>
                  <th style={{ padding: '12px 16px' }}>Status</th>
                  <th style={{ padding: '12px 16px' }}>Meta Message ID</th>
                  <th style={{ padding: '12px 16px' }}>HTTP</th>
                  <th style={{ padding: '12px 16px' }}>Sent Date</th>
                  <th style={{ padding: '12px 16px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((item) => (
                  <tr key={item._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a' }}>{item.recipientName || 'Customer'}</td>
                    <td style={{ padding: '12px 16px', color: '#334155' }}>{item.recipientContact}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#f0fdf4', color: '#166534', padding: '2px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
                        <Icon icon={item.channel === 'WhatsApp' ? 'mdi:whatsapp' : 'mdi:email'} /> {item.channel}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '3px 10px',
                          borderRadius: '12px',
                          fontSize: '11px',
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          background: item.status === 'Failed' ? '#fee2e2' : item.status === 'Delivered' || item.status === 'Read' ? '#dcfce7' : '#e0f2fe',
                          color: item.status === 'Failed' ? '#b91c1c' : item.status === 'Delivered' || item.status === 'Read' ? '#15803d' : '#0369a1'
                        }}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '12px', color: '#475569' }}>
                      {item.metaMessageId ? `${item.metaMessageId.substring(0, 16)}...` : '-'}
                    </td>
                    <td style={{ padding: '12px 16px', fontWeight: 700, color: item.httpStatusCode === 200 || item.httpStatusCode === 201 ? '#16a34a' : '#dc2626' }}>
                      {item.httpStatusCode || '-'}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#64748b' }}>
                      {item.sentAt ? new Date(item.sentAt).toLocaleString() : '-'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <button
                        onClick={() => setSelectedLog(item)}
                        style={{
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          background: '#f8fafc',
                          color: '#334155',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Icon icon="mdi:code-json" /> API Logs
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                Showing page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({totalCount} total logs)
              </span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: currentPage <= 1 ? 'not-allowed' : 'pointer' }}
                >
                  Previous
                </button>
                <button
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer' }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payload Modal */}
      {selectedLog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.65)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setSelectedLog(null)}
        >
          <div
            style={{
              background: '#ffffff',
              borderRadius: '16px',
              maxWidth: '750px',
              width: '100%',
              maxHeight: '85vh',
              overflowY: 'auto',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                Meta Graph API Debug Payload
              </h3>
              <button onClick={() => setSelectedLog(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '20px' }}>
                ✕
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px', fontSize: '13px' }}>
              <div><strong>Recipient:</strong> {selectedLog.recipientName} ({selectedLog.recipientContact})</div>
              <div><strong>Status:</strong> <span style={{ textTransform: 'uppercase', fontWeight: 700, color: selectedLog.status === 'Failed' ? '#dc2626' : '#16a34a' }}>{selectedLog.status}</span></div>
              <div><strong>Meta Message ID:</strong> <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px' }}>{selectedLog.metaMessageId || 'N/A'}</code></div>
              <div><strong>HTTP Status Code:</strong> {selectedLog.httpStatusCode || 'N/A'}</div>
              {selectedLog.errorCode && <div><strong>Error Code:</strong> <span style={{ color: '#dc2626', fontWeight: 700 }}>{selectedLog.errorCode}</span></div>}
              {selectedLog.errorMessage && <div style={{ gridColumn: 'span 2' }}><strong>Error Details:</strong> <span style={{ color: '#dc2626' }}>{selectedLog.errorMessage}</span></div>}
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>📤 Meta Graph API Request Payload Sent:</div>
              <pre style={{ background: '#0f172a', color: '#38bdf8', padding: '14px', borderRadius: '8px', fontSize: '12px', overflowX: 'auto', margin: 0 }}>
                {selectedLog.requestPayload ? JSON.stringify(selectedLog.requestPayload, null, 2) : '// No Request Payload Logged'}
              </pre>
            </div>

            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#475569', marginBottom: '6px' }}>📥 Meta Graph API Response Received:</div>
              <pre style={{ background: '#0f172a', color: '#4ade80', padding: '14px', borderRadius: '8px', fontSize: '12px', overflowX: 'auto', margin: 0 }}>
                {selectedLog.responsePayload ? JSON.stringify(selectedLog.responsePayload, null, 2) : selectedLog.errorLog ? selectedLog.errorLog : '// No Response Payload Logged'}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageLogsPage;
